/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/solid";

const CustomMultiSelect = ({ options, selectedValues, onChange, placeholder = "Select vendor types" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    const toggleDropdown = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = 250;
            setOpenUpward(spaceBelow < dropdownHeight);
        }
        setIsOpen((prev) => !prev);
    };

    const otherOptions = options.filter(opt => opt !== "Select All");
    const allOthersSelected = otherOptions.length > 0 && otherOptions.every(opt => selectedValues.includes(opt));

    const handleSelectAll = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (allOthersSelected) {
            onChange([]);
        } else {
            onChange([...otherOptions]);
        }
    };

    const handleOptionClick = (e, value) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (value === "Select All") {
            handleSelectAll(e);
            return;
        }
        
        const updatedValues = selectedValues.includes(value)
            ? selectedValues.filter((item) => item !== value)
            : [...selectedValues, value];
        onChange(updatedValues);
    };

    const isOptionChecked = (option) => {
        if (option === "Select All") {
            return allOthersSelected;
        }
        return selectedValues.includes(option);
    };

    const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setIsOpen(false);
        }
    };

    React.useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getDisplayText = () => {
        if (selectedValues.length === 0) {
            return placeholder;
        }
        if (allOthersSelected) {
            return "All vendor types";
        }
        if (selectedValues.length === 1) {
            return selectedValues[0];
        }
        return `${selectedValues.length} types selected`;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                ref={buttonRef}
                type="button"
                onClick={toggleDropdown}
                className="w-full p-2.5 border border-grey-300 rounded-lg cursor-pointer bg-white flex items-center justify-between hover:border-grey-400 transition-colors"
            >
                <span className={`text-sm truncate ${selectedValues.length === 0 ? 'text-grey-500' : 'text-grey-900'}`}>
                    {getDisplayText()}
                </span>
                <ChevronDownIcon className={`h-5 w-5 text-grey-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className={`absolute z-50 bg-white border border-grey-200 rounded-lg shadow-lg w-full min-w-[200px] ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                    <div className="max-h-48 overflow-y-auto py-1">
                        {options.map((option, idx) => {
                            const isChecked = isOptionChecked(option);
                            const isSelectAll = option === "Select All";
                            
                            return (
                                <div
                                    key={idx}
                                    onClick={(e) => handleOptionClick(e, option)}
                                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-grey-50 transition-colors select-none ${isSelectAll ? 'border-b border-grey-100 mb-1' : ''}`}
                                    role="option"
                                    aria-selected={isChecked}
                                >
                                    <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                        isChecked 
                                            ? 'bg-primary-500 border-primary-500' 
                                            : 'border-grey-300 bg-white'
                                    }`}>
                                        {isChecked && (
                                            <CheckIcon className="h-3 w-3 text-white" />
                                        )}
                                    </div>
                                    <span className={`text-sm ${isSelectAll ? 'font-medium text-grey-900' : 'text-grey-700'}`}>
                                        {option}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomMultiSelect;
